import { useEventBus } from "$root/event-bus";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { minutes } from "$root/utils";
import { PortfolioSnapshotStatus } from "@eu.mdotm.api/sphere";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * How long the page waits for the `investment-report-update` event before calling the generation lost.
 *
 * Deliberately the same 15 minutes `PortfolioSnapshotService.GENERATION_TIMEOUT` uses on the backend:
 * past that window the backend stops considering the generation in flight and would start a new one, so
 * a page that kept waiting would be waiting for an event nobody is going to send. This is also what
 * keeps the widgets from sitting in the pending state forever when the event never arrives at all —
 * a real possibility, since the gateway has no failure callback for fixed-date reports.
 */
const GENERATION_TIMEOUT = minutes(15);

export type AsOfSnapshotState =
	/** No as-of date: the page shows the live portfolio and no snapshot is involved. */
	| { kind: "live" }
	/** The snapshot is being computed. Every as-of read would 404, so widgets must not issue them. */
	| { kind: "generating" }
	/** The snapshot is stored: the as-of reads can be issued. */
	| { kind: "ready" }
	/**
	 * The snapshot could not be obtained — the request failed, or the event never came.
	 *
	 * `retry` asks for the same date again, and lives on this variant alone because it is the only one
	 * where asking again means anything: a generation in flight is joined rather than restarted, and a
	 * stored snapshot is already there. It is also the only way back, now that asking is an explicit
	 * action nothing retries on the page's behalf.
	 */
	| { kind: "error"; reason: "request-failed" | "timeout"; retry(): void };

/**
 * Whether a widget may issue its report reads right now (MDOT-24860).
 *
 * In the live view there is nothing to wait for. Pinned to a past date there is: until the snapshot is
 * stored the backend answers 404 for every read, so firing them would only fill the cache with errors
 * that the arrival of the data does not by itself clear. Widgets pass this to their query's `enabled`.
 *
 * Its counterpart is the wall in `PortfolioWidgetStatus` — this decides whether to *ask*, that decides
 * what to *show* meanwhile.
 */
export function isAsOfReadable(asOf: string | undefined, snapshot: AsOfSnapshotState | undefined): boolean {
	return asOf == null || snapshot?.kind === "ready";
}

/**
 * What a picked date's snapshot has to say about itself; `null` when it has nothing to say.
 *
 * The two failure reasons are told apart because what to do about them differs — the ask itself
 * failing is worth trying again now, a generation that never reported back is worth coming back to
 * later. They share the `Failed` qualifier, so anything keying off "the date failed" has one hook
 * rather than two.
 */
export type AsOfNotice = { qualifier: "Generating" } | { qualifier: "Failed"; reason: "request-failed" | "timeout" };

/**
 * Classifies a snapshot for the surfaces that report on it. Each renders its own wording — the bar
 * has room for a word, a dialog for a sentence — so this settles only whether there is something to
 * say and what it is called.
 */
export function asOfNotice(asOf: string | undefined, snapshot: AsOfSnapshotState | undefined): AsOfNotice | null {
	if (asOf == null || snapshot == null) {
		return null;
	}
	switch (snapshot.kind) {
		case "generating":
			return { qualifier: "Generating" };
		case "error":
			return { qualifier: "Failed", reason: snapshot.reason };
		default:
			return null;
	}
}

/** What the last ask for one date answered; `null` while that ask is in flight. */
type Answer = PortfolioSnapshotStatus | "request-failed" | null;

/**
 * Drives the Portfolio History snapshot behind the as-of view (MDOT-24860).
 *
 * Asking for a past date is a three-step conversation, not a read: the page asks the backend to make
 * the snapshot available, the backend answers READY or GENERATING, and a GENERATING answer is followed
 * — minutes later — by an `investment-report-update` event carrying the same date. This hook is that
 * conversation, so the widgets only ever see the four states above.
 *
 * The ask is a POST that commissions work, so it is fired from here rather than modelled as a query.
 * Nothing about a cache — a stale time, an invalidation, a window refocus, a retry policy — should be
 * able to start a multi-minute backend job, and while it was a query all four could: the default three
 * retries re-asked a backend that had already refused, and reported "computing…" for the seven seconds
 * of backoff it took them to drain. Every ask below is therefore deliberate and named.
 *
 * The gate is surface-level rather than per-widget because the snapshot is: the backend stores one
 * document per portfolio and date, sets all of its reports at once, and emits a single event when it
 * does. There is no moment at which one widget's data has landed and another's has not. Portfolio
 * Details and the Report Builder's preview each hold their own, for their own date.
 *
 * Changing the date while a generation is in flight needs no cancellation. State is keyed by the date,
 * so a new date is simply a different question; the abandoned generation still completes on the
 * backend and its snapshot is there, already READY, if the user comes back to that date.
 */
export function useAsOfSnapshot(uuid: string | undefined, asOf: string | undefined): AsOfSnapshotState {
	// One key per question asked. Everything below is keyed by it, so a date change cannot be answered
	// by the previous date's reply and no reset effect is needed to keep the two from mixing.
	const key = Boolean(uuid) && asOf != null ? `${uuid}|${asOf}` : null;

	const [answered, setAnswered] = useState<{ key: string; answer: Answer } | null>(null);
	const answer = answered?.key === key ? answered.answer : null;
	const isReady = answer === PortfolioSnapshotStatus.Ready;

	// Bumped to ask again: the completion event, a stream reconnect, or the user's own retry.
	const [attempt, setAttempt] = useState(0);
	const askAgain = useCallback(() => setAttempt((n) => n + 1), []);

	/**
	 * Records what an ask answered, except that READY is terminal: a stored snapshot for a past date is
	 * immutable, so no later answer — a failure least of all — can make it unreadable again. Without
	 * this, the re-ask the completion event triggers could wall off the very page it had just filled.
	 */
	const record = useCallback((askedKey: string, next: Answer) => {
		setAnswered((prev) =>
			prev?.key === askedKey && prev.answer === PortfolioSnapshotStatus.Ready ? prev : { key: askedKey, answer: next },
		);
	}, []);

	const askIdRef = useRef<string | null>(null);
	useEffect(() => {
		if (key == null || isReady) {
			return;
		}
		const askId = `${key}#${attempt}`;
		// One POST per intent. StrictMode mounts effects twice on the same instance, and this guard is
		// what keeps that from commissioning the job twice; a real remount gets a fresh ref and so does
		// ask again, which is what we want. There is deliberately no cleanup cancelling the call for the
		// same reason — the second invocation must not orphan the first one's answer. Answers from an
		// abandoned ask are dropped by the `askId` check below instead.
		if (askIdRef.current === askId) {
			return;
		}
		askIdRef.current = askId;
		record(key, null);
		portfolioRepository.asOfSnapshot(uuid!, asOf!).then(
			(snapshot) => {
				if (askIdRef.current === askId) {
					// `status` is optional in the generated response. An answer that does not say READY is
					// taken as GENERATING so the wait is at least bounded by the timeout below, rather
					// than hanging on a state nothing can move out of.
					record(key, snapshot.status ?? PortfolioSnapshotStatus.Generating);
				}
			},
			() => {
				if (askIdRef.current === askId) {
					record(key, "request-failed");
				}
			},
		);
	}, [key, uuid, asOf, attempt, isReady, record]);

	// Set once the wait for this date has run out. Keyed by date so returning to a timed-out date, or
	// moving to another one, starts a fresh wait rather than inheriting the previous verdict.
	const [timedOutOn, setTimedOutOn] = useState<string | null>(null);
	const isGenerating = key != null && answer === PortfolioSnapshotStatus.Generating && timedOutOn !== asOf;

	const timeoutStartedAtRef = useRef<{ asOf: string; at: number } | null>(null);
	useEffect(() => {
		if (!isGenerating || asOf == null) {
			timeoutStartedAtRef.current = null;
			return;
		}
		// Anchored to when this date's generation was first seen, so an ask that answers GENERATING
		// again (a reconnect, the completion event arriving early) does not hand the wait another full
		// window.
		if (timeoutStartedAtRef.current?.asOf !== asOf) {
			timeoutStartedAtRef.current = { asOf, at: Date.now() };
		}
		const elapsed = Date.now() - timeoutStartedAtRef.current.at;
		const timeoutId = setTimeout(() => setTimedOutOn(asOf), Math.max(0, GENERATION_TIMEOUT - elapsed));
		return () => clearTimeout(timeoutId);
	}, [isGenerating, asOf]);

	// Asks for the same date again, from the top: the timeout verdict and its anchor go, so a retry
	// gets a full window rather than expiring the moment it starts.
	const retry = useCallback(() => {
		setTimedOutOn(null);
		timeoutStartedAtRef.current = null;
		askAgain();
	}, [askAgain]);

	useEventBus("investment-report-update", {
		filter: (payload) => payload.uuid === uuid && payload.asOfDate === asOf,
		listener: () => {
			// Two separate things, now that asking is an action rather than a query. The widgets' cached
			// reads have to go — they may hold the 404 from a read issued before the data landed — and
			// the snapshot has to be asked again to learn that it is READY. The invalidation no longer
			// does the second as a side effect, which is the point: invalidating a cache must not be
			// able to commission a backend job.
			portfolioRepository.invalidateEntity(uuid!);
			askAgain();
		},
	});

	// A reconnect discards the browser's `Last-Event-ID` replay, so an event that landed while the
	// stream was down is simply gone. Ask again instead of waiting for one that will never be resent.
	useEventBus("server-events-stream-open", {
		listener: () => {
			if (isGenerating) {
				askAgain();
			}
		},
	});

	return useMemo((): AsOfSnapshotState => {
		if (key == null) {
			return { kind: "live" };
		}
		// Ahead of the failure branches, and `record` is what keeps it reachable: see its note.
		if (answer === PortfolioSnapshotStatus.Ready) {
			return { kind: "ready" };
		}
		if (answer === "request-failed") {
			return { kind: "error", reason: "request-failed", retry };
		}
		if (timedOutOn === asOf) {
			return { kind: "error", reason: "timeout", retry };
		}
		// Includes the initial load: before the backend has answered there is nothing to read either.
		return { kind: "generating" };
	}, [key, answer, timedOutOn, asOf, retry]);
}
